/**
 * <PokemonSprite> — single component every card uses to render a sprite.
 *
 * Why a wrapper instead of a bare <img>:
 *   - Centralizes the size variants (sm/md/lg) so we stay consistent
 *     across cards and don't get one card showing 22px sprites and
 *     another showing 36px sprites for the same context.
 *   - Centralizes the URL build via spriteUrlFor() so the
 *     VITE_ENABLE_SPRITES / VITE_SPRITE_SOURCE config touches exactly
 *     one place.
 *   - Centralizes the onError graceful-hide.  A 404 (e.g. a brand-new
 *     Pokémon PokeAPI hasn't mirrored yet, or LeekDuck renaming an
 *     icon path) sets display:none on the <img> so the layout stays
 *     intact instead of showing a broken-image icon.
 *   - Returns null when sprites are disabled OR no pokedex_id was
 *     provided.  Callers can sprinkle <PokemonSprite> liberally
 *     without worrying about whether the data is available.
 *
 * Two sources accepted:
 *   - `pokedexId` — preferred path, sprite URL is COMPUTED from
 *     spriteUrlFor() and so flips with VITE_SPRITE_SOURCE.
 *   - `src` — escape hatch for the ScrapedDuck cards where the URL
 *     comes from the data feed directly (raid_boss.image etc.)
 *     and we want to use that authoritative URL unchanged.  Still
 *     gated by VITE_ENABLE_SPRITES so the kill switch works
 *     uniformly.
 */
import { useState } from "react";
import { spriteUrlFor, spritesEnabled } from "../lib/sprites";

type SpriteSize = "sm" | "md" | "lg";

const SIZE_CLASSES: Record<SpriteSize, string> = {
  // Inline icon next to a name in a dense row.  Tight at 24px on
  // mobile so list rows don't get crushed, bumps to 28px at the
  // sm breakpoint where there's more horizontal room.
  sm: "w-6 h-6 sm:w-7 sm:h-7",
  // Default card-row sprite — raid bosses, eggs, etc.
  md: "w-9 h-9 sm:w-10 sm:h-10",
  // Hero sprite for a card whose subject is a single Pokémon
  // (PokemonCard, LoreCard, RankingCard headers).
  lg: "w-14 h-14 sm:w-16 sm:h-16",
};

interface PokemonSpriteProps {
  /** Pokédex ID — preferred path.  When set, URL is computed via
   * spriteUrlFor() and respects VITE_SPRITE_SOURCE. */
  pokedexId?: number | null;
  /** Explicit source URL — escape hatch for ScrapedDuck cards whose
   * image URL is published by the data feed itself.  Ignored when
   * pokedexId is also provided. */
  src?: string | null;
  /** Visual size variant.  See SIZE_CLASSES above for the actual
   * Tailwind classes per breakpoint. */
  size?: SpriteSize;
  /** Accessibility label — most often the Pokémon name.  Required
   * when the sprite is the only indication of which Pokémon a row
   * represents; can be empty when the name is right next to it (most
   * cases) so screen readers don't double-announce. */
  alt: string;
  /** Extra Tailwind classes to merge with the size + base classes. */
  className?: string;
}

export default function PokemonSprite({
  pokedexId,
  src,
  size = "md",
  alt,
  className = "",
}: PokemonSpriteProps) {
  const [errored, setErrored] = useState(false);

  // Build the URL.  pokedexId path is preferred; src is the override
  // for ScrapedDuck cards.  spritesEnabled() applies to BOTH paths so
  // the kill switch is uniform.
  const url = pokedexId
    ? spriteUrlFor(pokedexId)
    : src && spritesEnabled() ? src : null;

  if (!url || errored) return null;

  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setErrored(true)}
      // shrink-0 so this never gets crushed by neighboring flex
      // children — a sprite getting squished to 12px next to a long
      // name looks broken.  object-contain so the source aspect ratio
      // is preserved if the file isn't perfectly square.
      className={`shrink-0 object-contain ${SIZE_CLASSES[size]} ${className}`}
    />
  );
}
